import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { loadStorefrontPublic } from "../../services/storefrontPublicLoader";
import { applyStorefrontFavicon } from "../../utils/storefrontFavicon";
import { useVendorStore } from "../../context/VendorStoreContext";

const MULTI_VENDOR_TITLE = "Multi vendor system";

/**
 * Sets `document.title` and favicon.
 * Priority:
 *   1. Vendor slug URL active  → that vendor's name + logo
 *   2. Superuser single-vendor → superuser vendor's name + logo
 *   3. Default                 → platform title + static favicon
 */
const StorefrontDocumentTitle = () => {
  const { vendorStore } = useVendorStore();
  const { pathname } = useLocation();

  // When a vendor slug URL is active, instantly update title + favicon
  useEffect(() => {
    if (!vendorStore) return;
    const name =
      (typeof vendorStore.storeName === "string" && vendorStore.storeName.trim()) ||
      (typeof vendorStore.name === "string" && vendorStore.name.trim()) ||
      "Store";
    document.title = name;
    applyStorefrontFavicon(vendorStore.storeLogo || null);
  }, [vendorStore]);

  // When no vendor slug is active, fall back to superuser / platform defaults
  useEffect(() => {
    if (vendorStore) return; // handled by the effect above
    let cancelled = false;

    const apply = async () => {
      try {
        const res = await loadStorefrontPublic({ force: true });
        if (cancelled) return;

        if (res?.singleVendor && res?.vendor) {
          const v = res.vendor;
          const name =
            (typeof v.storeName === "string" && v.storeName.trim()) ||
            (typeof v.name === "string" && v.name.trim()) ||
            "Store";
          document.title = name;
          applyStorefrontFavicon(v.storeLogo);
        } else {
          document.title = MULTI_VENDOR_TITLE;
          applyStorefrontFavicon(null);
        }
      } catch {
        if (!cancelled) {
          document.title = MULTI_VENDOR_TITLE;
          applyStorefrontFavicon(null);
        }
      }
    };

    apply();
    return () => { cancelled = true; };
  }, [vendorStore, pathname]);

  return null;
};

export default StorefrontDocumentTitle;
